import CheckIcon from "@mui/icons-material/Check";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Badge,
  Box,
  Button,
  FormControl,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";

import { FilterIcon } from "@platformx/shared/static-assets";
import { ErrorTooltip, SearchBox, useAccess } from "@platformx/utilities";
import { t } from "i18next";
import { useState } from "react";

import { ContentListingHeaderContainer, FormControlCustom } from "./ContentListingHeader.styles";
import { useDropdownStyles } from "./DropdownHeader.style";
import ExpandingSearchBox from "./ExpandingSearchBox";
import {
  CONTENT_CONSTANTS,
  CONTENT_SORT_OPTIONS,
  DEFAULT_CONTENT_SORT,
  contentTypeBasedHideFilter,
  contentTypeSearchBox,
} from "./helperContentList";

const useStyles = makeStyles(() =>
  createStyles({
    rotateIcon: {
      animation: "$spin 5s linear infinite",
    },
    "@keyframes spin": {
      "0%": {
        transform: "rotate(360deg)",
      },
      "100%": {
        transform: "rotate(0deg)",
      },
    },
  }),
);

const ContentListingHeader = ({
  handleFilter,
  handleAddNew,
  title = "",
  category,
  subCategory,
  handleRefresh,
  animationState,
  filterValue,
  setsearchParam,
  handleSearch = () => {},
  onOpenDateFilter,
  activeFilterCount = 0,
  isDateFilterOpen = false,
  onSearchTerm,
  searchValue = "",
  sortValue = DEFAULT_CONTENT_SORT,
  onSortChange,
}: {
  title: string;
  handleSearch?: (e: any) => void;
  // eslint-disable-next-line no-unused-vars
  handleFilter: (filter: string) => void;
  handleAddNew: () => void;
  category: string;
  subCategory: string;
  handleRefresh: () => void;
  animationState: boolean;
  filterValue?: string;
  setsearchParam?: any;
  /** When provided, renders the date-filter button that opens the filter modal. */
  onOpenDateFilter?: () => void;
  /** Count shown in the date-filter button badge. */
  activeFilterCount?: number;
  /** Inverts the date-filter button while its modal is open (matches sort/filter). */
  isDateFilterOpen?: boolean;
  onSearchTerm?: (term: string) => void;
  searchValue?: string;
  /** Currently selected sort key (see CONTENT_SORT_OPTIONS). */
  sortValue?: string;
  /** When provided, renders the "Sort By" button + menu. Omitted for Course. */
  // eslint-disable-next-line no-unused-vars
  onSortChange?: (sort: string) => void;
}) => {
  const Class = useStyles();
  const classes = useDropdownStyles();
  const { canAccessAction } = useAccess();
  const [filterMenu, setFilterMenu] = useState<null | HTMLElement>(null);
  const openFilterMenu = Boolean(filterMenu);
  const [sortMenu, setSortMenu] = useState<null | HTMLElement>(null);
  const openSortMenu = Boolean(sortMenu);
  // A non-default sort surfaces a dot on the button, mirroring the date filter.
  const isSortActive = sortValue !== DEFAULT_CONTENT_SORT;
  const handleFilterClose = () => {
    setFilterMenu(null);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSortMenu(event.currentTarget);
  };
  const handleSortClose = () => {
    setSortMenu(null);
  };
  const handleSortSelect = (value: string) => {
    onSortChange?.(value);
    handleSortClose();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = () => (event.target as HTMLInputElement).value;
    handleFilter(selectedValue());
    handleFilterClose();
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setFilterMenu(event.currentTarget);
  };

  const hideFilter = contentTypeBasedHideFilter(title); //hide filter
  const hideSearchBox = contentTypeSearchBox(title) || false;
  return (
    <ContentListingHeaderContainer className={`contentListingHeader`}>
      <Box>
        <Typography variant='h3semibold' sx={{ textTransform: "capitalize" }}>
          {t(`${title}`)}
        </Typography>
      </Box>
      {title.toLocaleLowerCase() !== "result" && (
        <Box style={{ display: "flex", flex: 1, justifyContent: "flex-end" }}>
          {/* search box */}
          {hideSearchBox && (
            <Box style={{ marginRight: "10px" }}>
              <SearchBox
                handleSearch={handleSearch}
                setsearchParam={setsearchParam}
                setLoading={() => {}}
              />
            </Box>
          )}

          {onSearchTerm && (
            <Box
              sx={{
                marginRight: "10px",
                display: "flex",
                flex: { xs: 1, md: "0 0 auto" },
                justifyContent: "flex-end",
              }}>
              <ExpandingSearchBox
                value={searchValue}
                onSearch={onSearchTerm}
                placeholderLabel={title}
              />
            </Box>
          )}

          <Box
            onClick={handleRefresh}
            data-testid='content-listing-refresh'
            sx={{
              backgroundColor: "white",
              padding: { xs: "8px", md: "10px" },
              borderRadius: "4px",
              border: "1px solid #14142B",
              height: { xs: "42px", md: "46px" },
              width: "42px",
              alignItem: "center",
              cursor: "pointer",
              justifyContent: "center",
            }}>
            <SyncIcon
              sx={{
                color: "#2d2d39",
                verticalAlign: "middle",
                fontSize: { md: "21px" },
                cursor: "pointer",
              }}
              className={animationState ? Class.rotateIcon : ""}
            />
          </Box>

          {/* Superseded by the Status multi-select wherever the Filter-by modal exists. */}
          {hideFilter && !onOpenDateFilter && (
            <Box
              onClick={handleFilterClick}
              data-testid='content-listing-filter'
              sx={{
                backgroundColor: openFilterMenu ? "#2d2d39" : "white",
                padding: { xs: "8px", md: "10px" },
                marginLeft: "10px",
                borderRadius: "4px",
                border: "1px solid #14142B",
                display: "flex",
                height: { xs: "42px", md: "46px" },
                width: "42px",
                alignItems: "center",
                cursor: "pointer",
                justifyContent: "center",
                "&.openClass img": {
                  filter:
                    "brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(6197%) hue-rotate(110deg) brightness(97%) contrast(99%);",
                },
              }}
              className={openFilterMenu ? "openClass" : ""}>
              <img src={FilterIcon} alt='' width='17' height='24' />
            </Box>
          )}

          {/* Date filter button (opens the "Filter by:" modal). Rendered only
              when a handler is supplied, so Sites/Tags stay unchanged. */}
          {onOpenDateFilter && (
            <Box
              onClick={onOpenDateFilter}
              data-testid='content-listing-date-filter'
              sx={{
                backgroundColor: isDateFilterOpen ? "#2d2d39" : "white",
                padding: { xs: "8px", md: "10px" },
                marginLeft: "10px",
                borderRadius: "4px",
                border: "1px solid #14142B",
                display: "flex",
                height: { xs: "42px", md: "46px" },
                minWidth: "42px",
                gap: "6px",
                alignItems: "center",
                cursor: "pointer",
                justifyContent: "center",
              }}>
              <FilterAltOutlinedIcon
                sx={{ color: isDateFilterOpen ? "#ffffff" : "#2d2d39", fontSize: "21px" }}
              />
              <Typography
                variant='p3regular'
                sx={{
                  display: { xs: "none", md: "inline" },
                  color: isDateFilterOpen ? "#ffffff" : "#2d2d39",
                }}>
                {t("filter")}
              </Typography>

              {activeFilterCount > 0 && (
                <Badge
                  badgeContent={activeFilterCount}
                  data-testid='content-listing-date-filter-badge'
                  sx={{
                    "& .Platform-x-Badge-badge": {
                      position: "static",
                      transform: "none",
                      backgroundColor: "#0E7C3A",
                      color: "#ffffff",
                    },
                  }}
                />
              )}
            </Box>
          )}

          {/* Sort By button + menu (KIWI-6058). Rendered only when a handler is
              supplied, so content types without sort support (e.g. Course) are
              unaffected. */}
          {onSortChange && (
            <Box
              onClick={handleSortClick}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleSortClick(event as unknown as React.MouseEvent<HTMLElement>);
                }
              }}
              role='button'
              tabIndex={0}
              aria-label={t("sort_by")}
              aria-haspopup='menu'
              aria-expanded={openSortMenu}
              data-testid='content-listing-sort'
              sx={{
                backgroundColor: openSortMenu ? "#2d2d39" : "white",
                padding: { xs: "8px", md: "10px" },
                marginLeft: "10px",
                borderRadius: "4px",
                border: "1px solid #14142B",
                display: "flex",
                height: { xs: "42px", md: "46px" },
                minWidth: "42px",
                gap: "6px",
                alignItems: "center",
                cursor: "pointer",
                justifyContent: "center",
              }}>
              <SwapVertIcon
                sx={{ color: openSortMenu ? "#ffffff" : "#2d2d39", fontSize: "21px" }}
              />
              <Typography
                variant='p3regular'
                sx={{
                  display: { xs: "none", md: "inline" },
                  color: openSortMenu ? "#ffffff" : "#2d2d39",
                }}>
                {t("sort")}
              </Typography>
              {isSortActive && (
                <Box
                  data-testid='content-listing-sort-active-dot'
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#0E7C3A",
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          )}

          <Menu
            elevation={0}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            anchorEl={sortMenu}
            open={openSortMenu}
            onClose={handleSortClose}
            className={`${classes.FilterMenuWrapperItem} filterMenu`}
            PaperProps={{
              className: "filterMenuItem",
            }}>
            <Box sx={{ minWidth: "200px" }}>
              <Typography
                variant='h6medium'
                sx={{ padding: "10px 20px 5px", display: "block", color: "#14142B" }}>
                {t("sort_by")}
              </Typography>
              {CONTENT_SORT_OPTIONS.map((option) => {
                const selected = sortValue === option.value;
                return (
                  <MenuItem
                    key={option.value}
                    selected={selected}
                    onClick={() => handleSortSelect(option.value)}
                    data-testid={`content-listing-sort-option-${option.value}`}
                    sx={{
                      padding: "8px 20px",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                    }}>
                    <ListItemText
                      primary={
                        <Typography
                          variant='p2regular'
                          sx={{ fontWeight: selected ? 600 : 400, color: "#14142B" }}>
                          {t(option.labelKey)}
                        </Typography>
                      }
                    />
                    {selected && <CheckIcon sx={{ fontSize: "18px", color: "#14142B" }} />}
                  </MenuItem>
                );
              })}
            </Box>
          </Menu>

          <Menu
            elevation={0}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            anchorEl={filterMenu}
            open={openFilterMenu}
            onClose={handleFilterClose}
            className={`${classes.FilterMenuWrapperItem} filterMenu`}
            PaperProps={{
              className: "filterMenuItem",
              // elevation: 0,
            }}>
            <FormControl>
              <RadioGroup
                value={filterValue}
                onChange={handleChange}
                sx={{ textTransform: "capitalize" }}
                className='listViewContainer'>
                <FormControlCustom
                  className={`${filterValue === CONTENT_CONSTANTS.ALL ? "active" : ""} listView`}
                  value='ALL'
                  control={<Radio sx={{ display: "none" }} />}
                  label={<Typography variant='p2regular'>{t("all")}</Typography>}
                />
                <FormControlCustom
                  className={`${filterValue === CONTENT_CONSTANTS.PUBLISHED ? "active" : ""} listView`}
                  value='PUBLISHED'
                  control={<Radio sx={{ display: "none" }} />}
                  label={<Typography variant='p2regular'>{t("published")}</Typography>}
                />
                <FormControlCustom
                  className={`${filterValue === CONTENT_CONSTANTS.DRAFT ? "active" : ""} listView`}
                  value='DRAFT'
                  control={<Radio sx={{ display: "none" }} />}
                  label={<Typography variant='p2regular'>{t("draft")}</Typography>}
                />
                <FormControlCustom
                  className={`${filterValue === "UNPUBLISHED" ? "active" : ""} listView`}
                  value='UNPUBLISHED'
                  control={<Radio sx={{ display: "none" }} />}
                  label={<Typography variant='p2regular'>{t("unpublished")}</Typography>}
                />
                {title.toLowerCase() === "event" && (
                  <FormControlCustom
                    className={`${filterValue === "event" ? "active" : ""} listView`}
                    value='LIVE'
                    control={<Radio sx={{ display: "none" }} />}
                    label={<Typography variant='p2regular'>{t("live")}</Typography>}
                  />
                )}
              </RadioGroup>
            </FormControl>
          </Menu>
          <Box sx={{ display: { xs: "none", sm: "flex" }, marginLeft: "12px" }}>
            <ErrorTooltip
              component={
                <Button
                  className='addnewpage'
                  variant='primaryButton'
                  disabled={
                    !canAccessAction(category, subCategory, "Create")
                    //   || title === 'Course'
                  }
                  data-testid='addnew'
                  onClick={handleAddNew}>
                  {t("add_new")}
                </Button>
              }
              doAccess={
                !canAccessAction(category, subCategory, "Create")
                //||title === 'Course'
              }
            />
          </Box>
        </Box>
      )}
    </ContentListingHeaderContainer>
  );
};
export default ContentListingHeader;
